import type { Bundle } from 'fhir/r4';
import moment from 'moment';
import { useMemo } from 'react';
import { Loader, Message } from 'semantic-ui-react';
import useSWR from 'swr';
import type { ResourceEntry } from 'utils/bundleNarrowing';

import { useAuthContext } from '../../../contexts/AuthContext';
import FHIRFetcher from '../../../data/FHIRFetcher';

import { ADDENDUM_QUESTIONNAIRE_ID } from './constants';
import { isAddendumQuestionnaire } from './fhir-helpers';
import QuestionnaireResponseCard from './responses';
import type { QuestionnairesProps } from './types';

function Questionnaires(props: QuestionnairesProps) {
  const authInfo = useAuthContext();

  // Explicitly fetch the addendum questionnaire by ID.
  //
  // Unlike every other `useSWR`/`FHIRFetcher` pair here, this key is a FHIR
  // *read* (`/Questionnaire/{id}`), not a search — the proxy resolves the
  // resource itself, not a `Bundle`. `errorMessage` is not FHIR: it is the
  // ad-hoc envelope the `/fhir` proxy substitutes when the read fails, which
  // the guard below branches on.
  const { data: addendumQuestionnaire } = useSWR(
    [`/Questionnaire/${ADDENDUM_QUESTIONNAIRE_ID}`, authInfo.token],
    FHIRFetcher<fhir4.Questionnaire & { errorMessage?: string }>,
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );

  const augmentedQuestionnaireData = useMemo(() => {
    if (!props.questionnaireData || !props.questionnaireData.entry) return props.questionnaireData;

    // If we have the addendum questionnaire and it's not already in the list, add it
    if (addendumQuestionnaire && !addendumQuestionnaire.errorMessage) {
      const hasAddendum = props.questionnaireData.entry.some(
        (q: ResourceEntry<'Questionnaire'>) => isAddendumQuestionnaire(q.resource.id!)
      );

      if (!hasAddendum) {
        return {
          ...props.questionnaireData,
          entry: [
            ...props.questionnaireData.entry,
            {
              resource: addendumQuestionnaire,
              fullUrl: `Questionnaire/${ADDENDUM_QUESTIONNAIRE_ID}`,
            },
          ],
        };
      }
    }

    return props.questionnaireData;
  }, [props.questionnaireData, addendumQuestionnaire]);

  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data: Bundle | undefined) => {
      /*
       * `data?.entry?.length` is `number | undefined`, and `undefined > 0` is
       * always `false` — so this guard reads as if it tested emptiness but
       * actually tests "entry is present AND non-empty". That happens to be the
       * intended meaning, so the sort behaves correctly; the expression is only
       * accidentally right.
       *
       * The comparator's parameters are narrowed to
       * `ResourceEntry<'QuestionnaireResponse'>`: the bundle comes from a
       * `/QuestionnaireResponse` search, and the body reads `resource.authored`.
       */
      if (data?.entry?.length as number > 0) {
        //sort questionnaire response data so that newest will be on the top
        (data?.entry as ResourceEntry<'QuestionnaireResponse'>[] | undefined)?.sort(function (a: ResourceEntry<'QuestionnaireResponse'>, b: ResourceEntry<'QuestionnaireResponse'>) {
          return moment(b?.resource?.authored)?.valueOf() - moment(a?.resource?.authored)?.valueOf();
        });
      }
    },
  };
  //fetch Questionnaire Responses

  const {
    data: questionnaireResponseData,
    error: questionnaireResponseError,
    mutate: questionnaireResponseMutate,
  } = useSWR<fhir4.Bundle, Error>(
    [`/QuestionnaireResponse/?patient=${props.patientId}&_count=150&status:not=entered-in-error`, authInfo.token],
    // `FHIRFetcher` resolves `Bundle | undefined` for an empty endpoint, while swr
    // 0.5.7's fetcher parameter declares no `undefined`.
    FHIRFetcher<fhir4.Bundle> as () => Promise<fhir4.Bundle>,
    options
  );

  return (
    <div style={{ minHeight: '500px' }}>
      {!props.questionnaireData && <Loader active inline="centered" content="Loading Survey Data..." />}
      {!questionnaireResponseData && !questionnaireResponseError && (
        <Loader active inline="centered" content="Loading Survey Response Data..." />
      )}
      {/*
        SUSPECTED DEFECT — the card declares `patientId`, `org` and `setQRID`
        REQUIRED, and this, its only producer, can supply `undefined` for all
        three.

        `patientId` and `org` arrive from `patient/index.tsx` as `fhirPatient.id`
        (FHIR `Resource.id` is `0..1`) and `flatpatient.org`; `setQRID` is
        forwarded from that same parent. The guard on this JSX tests
        `questionnaireData`, `questionnaireResponseData` and
        `questionnaireData.entry` — it does not test any of these three.

        LATENT in practice: a patient page is only reachable with a resolved
        `Patient` resource, so `id` and `org` are populated by the time this
        renders. But nothing in the types or the guard enforces it, and the card
        interpolates `patientId` into FHIR search URLs, where an `undefined`
        would produce a `patient=undefined` query rather than an error.

        The assertions below record the pass as it happens today. Adding the
        guard that would make the contract true — skipping the card until all
        three are present — would stop rendering it in cases where it renders
        now.
      */}
      {props.questionnaireData && questionnaireResponseData && props.questionnaireData.entry && (
        <QuestionnaireResponseCard
          patientId={props.patientId as string}
          patientFirstName={props.patientFirstName}
          patientPhoneNumber={props.patientMobilePhone}
          patientEmail={props.patientEmail}
          org={props.org as string}
          profileMutate={props.profileMutate}
          questionnaireData={
            props.foodSurveyData && props.foodSurveyData.entry
              ? {
                  entry: augmentedQuestionnaireData!.entry!.concat(props.foodSurveyData.entry),
                }
              : augmentedQuestionnaireData!
          }
          questionnaireResponseData={questionnaireResponseData as { entry?: { resource: fhir4.QuestionnaireResponse }[] }}
          questionnaireResponseMutate={questionnaireResponseMutate}
          QRID={props.QRID}
          tasksMutate={props.tasksMutate}
          consentDataMutate={props.consentDataMutate}
          doNotContact={props.doNotContact}
          careTeamData={props.careTeamData}
          refreshTasks={props?.refreshTasks}
          fhirPatient={props.fhirPatient}
          refreshPatientDetails={props.refreshPatientDetails}
          practitionerData={props.practitionerData}
          setQRID={props.setQRID as (id: unknown) => void}
          allReferralData={props?.allReferralData}
          tasksData={props?.tasksData}
        />
      )}

      {questionnaireResponseError && (
        <Message negative>
          <Message.Header>Oops!. Something went wrong.. </Message.Header>
          <p>{questionnaireResponseError.message}</p>
        </Message>
      )}
    </div>
  );
}

export default Questionnaires;
